/**
 * FaoScraperDialog — Dialog d'admin pour le scraping FAO (100 % automatique).
 *
 * Flow :
 *   1. POST /api/fao/scraper/init {mode} → session_id (state: "solving")
 *      Le backend résout le contrôle anti-robot FAO automatiquement (~15-30 s)
 *      puis lance le scraping en arrière-plan.
 *   2. Polling /api/fao/scraper/{session_id}/status toutes les 2s
 *   3. Affichage progression / résultat / erreur
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { useCustomization } from '../contexts/CustomizationContext';
import { X, Loader2, CheckCircle, AlertCircle, RefreshCw, Shield } from '../lib/icons';

const API = process.env.REACT_APP_BACKEND_URL;

export const FaoScraperDialog = ({ open, mode = 'weekly', onClose }) => {
  const { getCurrentPalette, config } = useCustomization();
  const palette = getCurrentPalette();
  const colors = { ...palette.colors, ...config.colors.customOverrides };

  const [phase, setPhase] = useState('idle');     // idle | initializing | running | done | error
  const [sessionId, setSessionId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Auto-init on open
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    const init = async () => {
      setPhase('initializing');
      setError(null);
      try {
        const r = await axios.post(`${API}/api/fao/scraper/init`, { mode });
        if (cancel) return;
        setSessionId(r.data.session_id);
        setStatus(r.data);
        setPhase('running');
      } catch (e) {
        if (cancel) return;
        const msg = e?.response?.data?.detail || e.message || 'Erreur démarrage session FAO';
        setError(msg);
        setPhase('error');
      }
    };
    init();
    return () => { cancel = true; };
  }, [open, mode]);

  // Polling status when running (states backend: solving | running | done | error)
  useEffect(() => {
    if (phase !== 'running' || !sessionId) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/fao/scraper/${sessionId}/status`);
        setStatus(r.data);
        if (r.data.state === 'done') {
          setPhase('done');
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`FAO scrape terminé : ${r.data.n_new} nouveaux avis indexés`);
        } else if (r.data.state === 'error') {
          setPhase('error');
          setError(r.data.error);
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        // session expirée ? on stop le polling
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPhase('error');
        setError(e?.response?.data?.detail || 'Session expirée');
      }
    }, 2000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); pollRef.current = null; };
  }, [phase, sessionId]);

  const handleRetry = async () => {
    setPhase('initializing');
    setSessionId(null);
    setError(null);
    setStatus(null);
    try {
      const r = await axios.post(`${API}/api/fao/scraper/init`, { mode });
      setSessionId(r.data.session_id);
      setStatus(r.data);
      setPhase('running');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur ré-init');
      setPhase('error');
    }
  };

  const handleClose = async () => {
    if (sessionId && phase !== 'done') {
      try { await axios.post(`${API}/api/fao/scraper/${sessionId}/cancel`); } catch (e) { /* noop */ }
    }
    setPhase('idle');
    setSessionId(null);
    setError(null);
    setStatus(null);
    onClose?.();
  };

  const isSolving = status?.state === 'solving';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="fao-scraper-dialog">
        <div className="p-4" style={{ backgroundColor: colors.background, color: colors.text }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold flex items-center gap-2">
              <RefreshCw className="w-4 h-4" style={{ color: colors.primary }} />
              FAO — {mode === 'bulk' ? 'Import historique complet' : 'Mise à jour hebdomadaire'}
            </h2>
            <Button variant="ghost" size="icon" onClick={handleClose} data-testid="fao-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Phase: initializing */}
          {phase === 'initializing' && (
            <div className="flex flex-col items-center gap-3 py-6">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: colors.primary }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>Ouverture session FAO…</p>
            </div>
          )}

          {/* Phase: running (solving anti-robot ou scraping) */}
          {phase === 'running' && status && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2">
                {isSolving
                  ? <Shield className="w-5 h-5 animate-pulse" style={{ color: colors.primary }} />
                  : <Loader2 className="w-5 h-5 animate-spin" style={{ color: colors.primary }} />}
                <p className="text-sm font-medium" data-testid="fao-progress-label">
                  {isSolving ? 'Contrôle anti-robot FAO en cours…' : 'Scraping en cours…'}
                </p>
              </div>
              <p className="text-xs" style={{ color: colors.textMuted }}>{status.progress_msg}</p>
              {isSolving && (
                <p className="text-[10px]" style={{ color: colors.textMuted }}>
                  Le contrôle de sécurité de la FAO est résolu automatiquement (15-30 secondes).
                </p>
              )}
              {!isSolving && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                    <div className="text-lg font-bold">{status.n_scraped}</div>
                    <div className="text-[10px]" style={{ color: colors.textMuted }}>traités</div>
                  </div>
                  <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                    <div className="text-lg font-bold" style={{ color: colors.primary }}>{status.n_new}</div>
                    <div className="text-[10px]" style={{ color: colors.textMuted }}>nouveaux</div>
                  </div>
                  <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                    <div className="text-lg font-bold">{status.n_updated}</div>
                    <div className="text-[10px]" style={{ color: colors.textMuted }}>maj</div>
                  </div>
                </div>
              )}
              <p className="text-[10px]" style={{ color: colors.textMuted }}>
                Vous pouvez fermer cette fenêtre, le scrape continue en arrière-plan.
              </p>
            </div>
          )}

          {/* Phase: done */}
          {phase === 'done' && status && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" style={{ color: '#16a34a' }} />
                <p className="text-sm font-medium">Scrape terminé</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                  <div className="text-lg font-bold">{status.n_scraped}</div>
                  <div className="text-[10px]" style={{ color: colors.textMuted }}>traités</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                  <div className="text-lg font-bold" style={{ color: '#16a34a' }}>{status.n_new}</div>
                  <div className="text-[10px]" style={{ color: colors.textMuted }}>nouveaux</div>
                </div>
                <div className="p-2 rounded border" style={{ borderColor: colors.border }}>
                  <div className="text-lg font-bold">{status.n_updated}</div>
                  <div className="text-[10px]" style={{ color: colors.textMuted }}>maj</div>
                </div>
              </div>
              <Button onClick={handleClose} className="w-full" style={{ backgroundColor: colors.primary, color: 'white' }} data-testid="fao-done-close-btn">
                Fermer
              </Button>
            </div>
          )}

          {/* Phase: error */}
          {phase === 'error' && (
            <div className="space-y-3 py-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" style={{ color: '#dc2626' }} />
                <p className="text-sm font-medium">Erreur</p>
              </div>
              <p className="text-xs p-2 rounded border" style={{ borderColor: '#dc2626', backgroundColor: '#fef2f2', color: '#7f1d1d' }} data-testid="fao-error-msg">
                {error || 'Erreur inconnue'}
              </p>
              <div className="flex gap-2">
                <Button onClick={handleRetry} variant="outline" className="flex-1" data-testid="fao-retry-btn">
                  Réessayer
                </Button>
                <Button onClick={handleClose} className="flex-1" style={{ backgroundColor: colors.primary, color: 'white' }} data-testid="fao-error-close-btn">
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FaoScraperDialog;
